<template>
  <div class="chart-style-panel" style="overflow-x: hidden;">
    <div style="height: 300px; overflow-x: scroll; overflow-y: scroll; border: 0.5px solid lightgrey;">
      <ChartEditor :key="editorKey" />
    </div>

    <Button type="button" class="full-width-btn" @click="chartDataEditorVisible = true">
      <IconEdit class="btn-icon"/>
      Expand chart data editor
    </Button>

    <Divider/>

    <!-- All Charts -->

    <div class="row">
      <div style="flex: 1;">Title</div>
      <InputGroup style="flex: 4;">
        <InputText placeholder="Add Chart Title" v-model="chartTitle" style="margin-left: 10px;"/>
        <InputGroupAddon>
          <ColorButton :width="`20px`" :height="`20px`" :showIcon="true"
                       :color="selectedWidget.options.chartTitleColor ?? '#000'"
                       @click="$refs.titlePickerVisible.toggle($event)" style="flex:3;"/>
          <OverlayPanel ref="titlePickerVisible">
            <ColorPicker v-model="chartTitleColor"/>
          </OverlayPanel>
        </InputGroupAddon>
      </InputGroup>
    </div>


    <div class="row">
      <div style="flex: 1;">Type</div>
      <SelectButton style="flex: 3;" v-model="chartType" :options="chartTypeOptions" optionLabel="value"
                    dataKey="value">
        <template #option="slotProps">
          <component :is="slotProps.option.icon" size="16"/>
        </template>
      </SelectButton>
    </div>

    <div class="row">
      <div style="flex: 1;">Data</div>
      <Dropdown v-model="dataTypeChosen"
                :options="dataTypeOptions"
                optionValue="value"
                optionLabel="name"
                style="flex: 3;"
                @change="updateOptions({ dataType: dataTypeChosen })"/>
    </div>

    <Divider/>

    <!-- Chart Specific stuff -->

    <template v-if="selectedWidget.chartType === 'line'">
      <div class="row-gap">
        <div style="flex: 1;">Style：</div>
        <SelectButton
            style="flex: 3;"
            @change="updateLineChartStyle($event.value)"
            v-model="lineChartStyle"
            option-value="value"
            option-label="label"
            :options="lineChartStyleOptions"
        />
      </div>
    </template>

    <div class="row-gap" v-if="selectedWidget.chartType === 'column'">
      <div>Style：</div>
      <SelectButton
          @change="updateBarChartStyle($event.value)"
          v-model="barChartStyle"
          option-value="value"
          option-label="label"
          :options="barChartStyleOptions"
      />
    </div>

    <div class="row">
      <div class="chk-box">
        <Checkbox v-model="aiButton" :binary="true" style="margin-right: 10px;"/>
        Add AI Chat Button
      </div>
    </div>

    <div class="row" v-if="selectedWidget.chartType === 'pie'">
      <div class="chk-box">
        <Checkbox
            @change="updateOptions({ donut: donut })"
            binary
            v-model="donut"
            inputId="donutStyle"
        >Donut Chart Style
        </Checkbox>
        <label for="donutStyle">Donut chart style</label>
      </div>
    </div>

    <div class="row" v-if="selectedWidget.chartType === 'pie' && selectedWidget.options.donut">
      <div class="label" style="flex: 2;">Donut Size：</div>
      <Slider style="flex: 3;" :min="10" :max="80" :step="5" v-model="donutSize"/>
    </div>

    <div class="row">
      <div class="chk-box">
        <Checkbox
            v-model="filterOutZeros"
            inputId="filterOutZeros"
            binary
        />
        <label for="filterOutZeros">Filter Out Zeros</label>
      </div>
    </div>

    <div class="row" v-if="!selectedWidget.options.stackBars">
      <div class="chk-box">
        <Checkbox
            v-model="filterOutNegatives"
            inputId="filterOutNegatives"
            binary
        />
        <label for="filterOutNegatives">Filter Out Negatives</label>
      </div>
    </div>


    <div class="row" v-if="selectedWidget.chartType === 'column' && barChartStyle === 'waterfall'">
      <div class="chk-box">
        <Checkbox
            v-model="showPriceValue"
            inputId="showPriceValue"
            binary
        />
        <label for="showPriceValue">Show Price/Value Column</label>
      </div>
    </div>

    <div class="row">
      <div class="chk-box">
        <Checkbox
            v-model="updateNumberFormat"
            inputId="compactNumberFormat"
            binary
        />
        <label for="compactNumberFormat">Compact number formatting</label>
      </div>
    </div>

    <Divider/>

    <!-- Legend Options -->
    <div>
      <h6>Legend</h6>
      <div class="chk-box">
        <Checkbox
            @change="updateOptions({ legendEnabled: legendEnabled })"
            binary
            v-model="legendEnabled"
            id="legend"
        />
        <label for="legend">Show Legend</label>
      </div>


      <div v-if="legendEnabled">
        <div class="row">
          <div style="flex: 1;">Position</div>
          <SelectButton
              style="flex: 3;"
              @change="$event.value ? updateOptions({ legend: $event.value }): ''"
              v-model="legend"
              :options="legendOptions"
          />
        </div>

        <div class="row">
          <div style="flex: 1;">Layout</div>
          <SelectButton
              style="flex: 3;"
              @change="$event.value ? updateOptions({ legendLayout: $event.value }) : ''"
              v-model="legendLayout"
              :options="legendLayoutOptions"
          />
        </div>

        <div class="row">
          <div style="flex: 1;">Align</div>
          <SelectButton
              style="flex: 3;"
              @change="$event.value ? updateOptions({ legendAlign: $event.value }) : ''"
              v-model="legendAlign"
              :options="legendAlignOptions"
          />
        </div>
      </div>

      <Divider/>
    </div>

    <!-- Axis/Tooltip/Data Labels -->

    <div class="row" v-if="selectedWidget.chartType === 'column' && selectedWidget.options.stackBars">

      <div class="chk-box">
        <Checkbox
            @change="updateOptions({ stackLabels: stackLabels })"
            binary
            v-model="stackLabels"
            inputId="stackLabelsStyle"
        />
        <label for="stackLabelsStyle">Show Stack Labels</label>
      </div>
    </div>
    <div>
      <h6>Tooltips</h6>
      <div class="chk-box" style="margin-right: 30px">
        <Checkbox @change="updateOptions({ tooltips: tooltips })"
                  binary
                  v-model="tooltips"
                  inputId="tooltips"
        >
        </Checkbox>
        <label for="tooltips">Show Tooltips</label>
      </div>
      <div v-if="tooltips" class="row">
        <div style="flex: 2;">Font Size</div>


        <Dropdown v-model="tooltipFontSize"
                  :options="fontSizeOptions"
                  style="flex: 4;"
                  placeholder="Select Font Size"
                  @change="updateOptions({ tooltipFontSize: tooltipFontSize })"/>
      </div>

      <div v-if="tooltips" class="row">
        <div style="flex: 2;">Font Color</div>

        <ColorButton
            :width="`100px`" :showIcon="false"
            :color="selectedWidget.options.tooltipFontColor ?? '#000'"
            @click="$refs.toolFontColor.toggle($event)" style="flex:3;margin-left: 10px;"/>
        <OverlayPanel ref="toolFontColor">
          <ColorPicker v-model="tooltipFontColor"/>
        </OverlayPanel>
      </div>
    </div>
    <Divider/>
    <div>
      <h6>Data Labels</h6>
      <div class="row">
        <div class="chk-box" style="margin-right: 30px">

          <Checkbox @change="updateOptions({ dataLabels: dataLabels })"
                    binary
                    v-model="dataLabels"
                    inputId="dataLabels"
          >
          </Checkbox>
          <label for="dataLabels">Show Data Labels</label>
        </div>
      </div>

      <div class="row">
        <div class="label-long" style="flex: 1;">Type</div>
        <SelectButton style="flex: 4;" v-model="dataLabelType"
                      @change="$event.value ? updateOptions({ dataLabelType: $event.value }) : 'Name'"
                      :options="dataLabelOptions"
        />
      </div>
      <div v-if="selectedWidget.chartType !== 'line'" class="row">
        <div class="label-long" style="flex: 1;">Location</div>
        <SelectButton style="flex: 4;" v-model="dataLabelLocation"
                      @change="$event.value ? updateOptions({ dataLabelLocation: $event.value }) : 'Inside'"
                      :options="labelLocationOptions"
        />
      </div>
    </div>
    <div class="group" v-if="selectedWidget.chartType === 'column' || selectedWidget.chartType === 'line' ">
      <div class="chk-box" style="margin-right: 10px">
        <Checkbox @change="updateOptions({ axisX: xAxis })"
                  binary
                  v-model="xAxis"
                  inputId="xAxis"
        >
        </Checkbox>
        <label for="xAxis">Show X axis</label>

        <InputGroup style="flex: 2;" v-if="xAxis">
          <InputText placeholder="X axis title" v-model="xAxisLabel" style="margin-left: 10px;"/>
          <InputGroupAddon>
            <ColorButton :width="`20px`" :height="`20px`" :showIcon="true"
                         :color="selectedWidget.options.axisXLabelColor ?? '#000'"
                         @click="$refs.xaxisPickerVisible.toggle($event)" style="flex:3;"/>
            <OverlayPanel ref="xaxisPickerVisible">
              <ColorPicker v-model="axisXLabelColor"/>
            </OverlayPanel>
          </InputGroupAddon>
        </InputGroup>

      </div>

      <div class="row">
        <label for="xAxisLabelEnabled" style="flex:2">X axis Label Rotation</label>
        <InputNumber v-model="xAxisLabelRotation" showButtons class="border-inputs" :step="5" :style="{ flex: 2 }"/>
      </div>

      <div class="chk-box" style="margin-right: 10px;">
        <Checkbox @change="updateOptions({ axisXLabelEnabled: xAxisLabelEnabled })"
                  binary
                  v-model="xAxisLabelEnabled"
                  inputId="xAxisLabelEnabled"
        >
        </Checkbox>
        <label for="xAxisLabelEnabled">Show X axis Labels</label>
      </div>

      <div class="chk-box" style="margin-right: 10px">
        <Checkbox @change="updateOptions({ axisY: yAxis })"
                  binary
                  v-model="yAxis"
                  inputId="yAxis"
        >
        </Checkbox>
        <label for="yAxis">Show Y axis</label>

        <InputGroup style="flex: 2;width:100px" v-if="yAxis">
          <InputText placeholder="Y axis title" v-model="yAxisLabel" style="margin-left: 10px;"/>
          <InputGroupAddon>
            <ColorButton :width="`20px`" :height="`20px`" :showIcon="true"
                         :color="selectedWidget.options.axisYLabelColor ?? '#000'"
                         @click="$refs.yaxisPickerVisible.toggle($event)" style="flex:3;"/>
            <OverlayPanel ref="yaxisPickerVisible">
              <ColorPicker v-model="axisYLabelColor"/>
            </OverlayPanel>
          </InputGroupAddon>
        </InputGroup>
      </div>

      <div class="chk-box" style="margin-right: 10px;">
        <Checkbox @change="updateOptions({ axisYLabelEnabled: yAxisLabelEnabled })"
                  binary
                  v-model="yAxisLabelEnabled"
                  inputId="yAxisLabelEnabled"
        >
        </Checkbox>
        <label for="xAxisLabelEnabled">Show Y axis Labels</label>
      </div>

      <div class="chk-box" style="margin-right: 10px;margin-top:10px;">
        <Checkbox @change="updateOptions({ plotYAxis: plotYAxis })"
                  binary
                  v-model="plotYAxis"
                  inputId="plotYAxis"
        >
        </Checkbox>
        <label for="plotYAxis">Plot Y axis</label>

        <InputGroup style="flex: 2;width:100px" v-if="plotYAxis">
          <InputNumber placeholder="Plot value" v-model="plotYAxisValue" style="margin-left: 10px;"/>
          <InputGroupAddon>
            <ColorButton :width="`20px`" :height="`20px`" :showIcon="true"
                         :color="selectedWidget.options.plotYAxisColor ?? '#000'"
                         @click="$refs.plotYAxisPickerVisible.toggle($event)" style="flex:3;"/>
            <OverlayPanel ref="plotYAxisPickerVisible">
              <ColorPicker v-model="plotYAxisColor"/>
            </OverlayPanel>
          </InputGroupAddon>
        </InputGroup>
      </div>

    </div>

    <!-- Text -->
    <div class="row">

      <div style="flex: 2;">Font Size</div>

      <Dropdown v-model="selectedWidget.fontSize"
                :options="fontSizeOptions"
                style="flex: 4;"
                placeholder="Select Font Size"
                @change="value => updateOptions({ fontSize: value.value })"/>
    </div>
    <div class="row">
      <div style="flex: 2;">Font Color</div>
      <ColorButton v-if="dataLabels" :width="`100px`" :showIcon="false"
                   :color="selectedWidget.options.dataLabelsColor ?? '#000'"
                   @click="$refs.labelPickerVisible.toggle($event)" style="flex:3;margin-left: 10px;"/>
      <OverlayPanel ref="labelPickerVisible">
        <ColorPicker v-model="dataLabelsColor"/>
      </OverlayPanel>
    </div>

    <div v-if="selectedWidget.chartType === 'column' || selectedWidget.chartType === 'line' ">
      <div v-if="xAxis">
        <div class="style-row">
          <span class="label-long">X Axis Grid</span>
          <InputNumber v-model="selectedWidget.options.xAxisGridWidth" showButtons class="dimension-input"/>
          <div style="width: 70px; display: inline-block">
            <ColorButton @click="xAxisColorPickerVisible.toggle($event)"
                         :color="selectedWidget.options.xAxisGridColor ?? 'transparent'" style="flex: 3"/>
            <OverlayPanel ref="xAxisColorPickerVisible">
              <ColorPicker v-model="selectedWidget.options.xAxisGridColor"/>
            </OverlayPanel>
          </div>
          <div style="margin-left: 10px; display: inline-block">
            <Button v-if="selectedWidget.options.xAxisGridWidth != null" icon="pi pi-trash"
                    @click="selectedWidget.options.xAxisGridWidth = null"></Button>
          </div>
        </div>
      </div>

      <div v-if="yAxis">
        <div class="style-row">
          <span class="label-long">Y Axis Grid</span>
          <InputNumber v-model="selectedWidget.options.yAxisGridWidth" showButtons class="dimension-input"/>
          <div style="width: 70px; display: inline-block">
            <ColorButton @click="yAxisColorPickerVisible.toggle($event)"
                         :color="selectedWidget.options.yAxisGridColor ?? 'transparent'" style="flex: 3"/>
            <OverlayPanel ref="yAxisColorPickerVisible">
              <ColorPicker v-model="selectedWidget.options.yAxisGridColor"/>
            </OverlayPanel>
          </div>
          <div style="margin-left: 10px; display: inline-block">
            <Button v-if="selectedWidget.options.yAxisGridWidth != null" icon="pi pi-trash"
                    @click="selectedWidget.options.yAxisGridWidth = null"></Button>
          </div>
        </div>
      </div>
    </div>


    <div class="style-row"
         v-if="selectedWidget.chartType === 'bar' || barChartStyle === 'waterfall' || selectedWidget.chartType === 'column'">
      <span class="label-long">Bar Thickness</span>
      <InputNumber
          v-model="barThickness"
          showButtons
          :step="1"
          class="dimension-input"
      />
      <Button v-if="barThickness != null" icon="pi pi-trash" @click="barThickness = null"></Button>
    </div>

    <Divider/>

    <!-- Theme -->
    <div class="row" style="width: 300px" v-for="(color, index) in themeColor" :key="index">
      <div style="flex: 2;">{{ index === 0 ? 'Theme Color Matching' : '' }}</div>
      <div class="color-btn-wrap" style="flex: 4;">
        <ColorButton @click="$refs.themeColorPickerVisible[index].toggle($event)" :color="color" style="width: 150px;"/>
        <div class="delete-color-btn" @click.stop="deleteThemeColor(index)" v-if="index !== 0" style="width: 20px;">
          <IconCloseSmall/>
        </div>
      </div>
      <OverlayPanel ref="themeColorPickerVisible">
        <ColorPicker
            :modelValue="color"
            @update:modelValue="value => updateTheme(value, index)"
        />
      </OverlayPanel>
    </div>
  </div>
  <div class="row">
    <OverlayPanel ref="presetThemesVisible">
      <div class="preset-themes">
        <div class="preset-theme" v-for="(item, index) in presetChartThemes" :key="index">
          <div
              class="preset-theme-color"
              :class="{ 'select': presetThemeColorHoverIndex[0] === index && itemIndex <= presetThemeColorHoverIndex[1] }"
              v-for="(color, itemIndex) in item"
              :key="color"
              :style="{ backgroundColor: color }"
              @click="applyPresetTheme(item, itemIndex)"
              @mouseenter="presetThemeColorHoverIndex = [index, itemIndex]"
              @mouseleave="presetThemeColorHoverIndex = [-1, -1]"
          ></div>
        </div>
      </div>

    </OverlayPanel>
    <div class="btn-group">
      <Button @click="$refs.presetThemesVisible.toggle($event)" class="btn" style="flex: 2;">Preset themes</Button>
      <Button
          class="btn"
          :disabled="themeColor.length >= 10"
          style="flex: 3;"
          @click="addThemeColor()"
      >
        <IconPlus class="btn-icon"/>
        Add theme color
      </Button>
    </div>
  </div>

  <ElementOutline/>

  <Divider/>

  <Dialog v-model:visible="chartDataEditorVisible" maximizable modal header="Edit Chart Data"
          :style="{ width: '50rem' }" :breakpoints="{ '1199px': '75vw', '575px': '90vw' }">
    <ChartEditor/>
  </Dialog>

</template>

<script lang="ts" setup>
import {computed, onUnmounted, ref, watch} from 'vue'
import {storeToRefs} from 'pinia'
import {useSlidesStore, useWidgetStore} from '@/store'
import {ChartOptions} from '@/types/slides'
import emitter, {EmitterEvents} from '@/utils/emitter'
import ElementOutline from '../../common/ElementOutline.vue'
import ColorButton from '../../common/ColorButton.vue'
import ColorPicker from '@/components/ColorPicker/index.vue'
import ChartEditor from "@/views/Editor/Toolbar/ElementStylePanel/ChartStylePanel/ChartEditor.vue";
import InputNumber from "primevue/inputnumber";
import Button from "primevue/button";
import tinycolor from "tinycolor2";

const presetChartThemes = [
  ['#d87c7c', '#919e8b', '#d7ab82', '#6e7074', '#61a0a8', '#efa18d'],
  ['#dd6b66', '#759aa0', '#e69d87', '#8dc1a9', '#ea7e53', '#eedd78'],
  ['#516b91', '#59c4e6', '#edafda', '#93b7e3', '#a5e7f0', '#cbb0e3'],
  ['#893448', '#d95850', '#eb8146', '#ffb248', '#f2d643', '#ebdba4'],
  ['#4ea397', '#22c3aa', '#7bd9a5', '#d0648a', '#f58db2', '#f2b3c9'],
  ['#3fb1e3', '#6be6c1', '#626c91', '#a0a7e6', '#c4ebad', '#96dee8'],
  ['#fc97af', '#87f7cf', '#f7f494', '#72ccff', '#f7c5a0', '#d4a4eb'],
  ['#c1232b', '#27727b', '#fcce10', '#e87c25', '#b5c334', '#fe8463'],
  ['#2ec7c9', '#b6a2de', '#5ab1ef', '#ffb980', '#d87a80', '#8d98b3'],
  ['#e01f54', '#001852', '#f5e8c8', '#b8d2c7', '#c6b38e', '#a4d8c2'],
  ['#c12e34', '#e6b600', '#0098d9', '#2b821d', '#005eaa', '#339ca8'],
  ['#8a7ca8', '#e098c7', '#8fd3e8', '#71669e', '#cc70af', '#7cb4cc'],
]

const slidesStore = useSlidesStore()
const {theme} = storeToRefs(slidesStore)
const {selectedWidget} = storeToRefs(useWidgetStore())

const chartDataEditorVisible = ref(false)
const presetThemesVisible = ref(false)
const presetThemeColorHoverIndex = ref<[number, number]>([-1, -1])

const barChartStyle = computed(() => {
  if (selectedWidget.value.options.columnBars) return 'columnBars';
  if (selectedWidget.value.options.horizontalBars) return 'horizontalBars';
  if (selectedWidget.value.options.stackBars) return 'stackBars';
  if (selectedWidget.value.options.waterfall) return 'waterfall';
  return '';
});

const xAxisColorPickerVisible = ref()
const yAxisColorPickerVisible = ref()

const lineChartStyle = computed(() => {
  if (selectedWidget.value.options.area) return 'area';
  if (selectedWidget.value.options.scatter) return 'scatter';
  if (selectedWidget.value.options.smooth) return 'smooth';
  return '';
});

const barChartStyleOptions = ref([
  {label: 'Vertical', value: 'columnBars'},
  {label: 'Horizontal', value: 'horizontalBars'},
  {label: 'Stacked', value: 'stackBars'},
  {label: 'Waterfall', value: 'waterfall'},
]);

const lineChartStyleOptions = ref([
  {label: 'Area', value: 'area'},
  {label: 'Scatter', value: 'scatter'},
  {label: 'Smooth', value: 'smooth'},
]);

const fill = ref<string>('#000')

const themeColor = ref<string[]>([])

const legendEnabled = computed({
  get() {
    return selectedWidget.value.options.legendEnabled ?? false
  },
  set(val) {
    selectedWidget.value.options.legendEnabled = val
  }
})

const legend = computed(() => selectedWidget.value.options?.legend || 'Bottom')
const legendLayout = computed(() => selectedWidget.value.options?.legendLayout || 'Horizontal')
const legendAlign = computed(() => selectedWidget.value.options?.legendAlign || 'Left')
const legendOptions = ref(['Top', 'Bottom']);
const legendAlignOptions = ref(['Left', 'Center', 'Right']);
const legendLayoutOptions = ref(['Horizontal', 'Vertical']);

const chartTypeOptions = ref([
  {icon: 'IconChartPie', value: 'pie'},
  {icon: 'IconChartHistogram', value: 'column'},
  {icon: 'IconChartLine', value: 'line'}
])

const chartType = computed({
  get() {
    return chartTypeOptions.value.find(item => item.value === selectedWidget.value?.chartType) ?? 'pie'
  },
  set(val) {
    if (val) {
      selectedWidget.value.chartType = val.value
      if (val.value !== 'bar') {
        selectedWidget.value.themeColor[0] = '#5b9bd5'
      }
    }
  }
})

const chartTitle = computed({
  get() {
    return selectedWidget.value?.chartTitle ?? ''
  },
  set(val) {
    selectedWidget.value.chartTitle = val
  }
})

const chartTitleColor = computed({
  get() {
    return selectedWidget.value?.options.chartTitleColor ?? ''
  },
  set(val) {
    selectedWidget.value.options.chartTitleColor = val
  }
})

const lineSmooth = ref(true)
const showLine = ref(true)
const showArea = ref(false)

const donut = computed({
  get() {
    return selectedWidget.value.options.donut ?? false
  },
  set(val) {
    selectedWidget.value.options.donut = val
  }
})

const donutSize = computed({
  get() {
    return selectedWidget.value.options.donutSize ?? 50
  },
  set(val) {
    selectedWidget.value.options.donutSize = val
  }
})


const compactValue = ref()

const barThickness = computed({
  get() {
    return selectedWidget.value.options.barThickness
  },
  set(val) {
    selectedWidget.value.options.barThickness = val
  }
})

const yAxis = computed({
  get() {
    return selectedWidget.value?.options.axisY ?? true
  },
  set(val) {
    selectedWidget.value.options.axisY = val
  }
})

const plotYAxis = computed({
  get() {
    return selectedWidget.value?.options.plotYAxis ?? false
  },
  set(val) {
    selectedWidget.value.options.plotYAxis = val
  }
})

const xAxis = computed({
  get() {
    return selectedWidget.value?.options.axisX ?? true
  },
  set(val) {
    selectedWidget.value.options.axisX = val
  }
})

const yAxisLabelEnabled = computed({
  get() {
    return selectedWidget.value?.options.axisYLabelEnabled ?? true
  },
  set(val) {
    selectedWidget.value.options.axisYLabelEnabled = val
  }
})

const yAxisLabel = computed({
  get() {
    return selectedWidget.value?.options.axisYLabel ?? ''
  },
  set(val) {
    selectedWidget.value.options.axisYLabel = val
  }
})


const plotYAxisValue = computed({
  get() {
    return selectedWidget.value?.options.plotYAxisValue ?? 0
  },
  set(val) {
    selectedWidget.value.options.plotYAxisValue = val
  }
})


const axisYLabelColor = computed({
  get() {
    return selectedWidget.value?.options.axisYLabelColor
  },
  set(val) {
    selectedWidget.value.options.axisYLabelColor = val
  }
})

const plotYAxisColor = computed({
  get() {
    return selectedWidget.value?.options.plotYAxisColor
  },
  set(val) {
    selectedWidget.value.options.plotYAxisColor = val
  }
})

const xAxisLabelEnabled = computed({
  get() {
    return selectedWidget.value?.options.axisXLabelEnabled ?? true
  },
  set(val) {
    selectedWidget.value.options.axisXLabelEnabled = val
  }
})

const xAxisLabel = computed({
  get() {
    return selectedWidget.value?.options.axisXLabel ?? ''
  },
  set(val) {
    selectedWidget.value.options.axisXLabel = val
  }
})

const xAxisLabelRotation = computed({
  get() {
    return selectedWidget.value?.options.xAxisLabelRotation ?? -45
  },
  set(val) {
    selectedWidget.value.options.xAxisLabelRotation = val
  }
})

const axisXLabelColor = computed({
  get() {
    return selectedWidget.value?.options.axisXLabelColor
  },
  set(val) {
    selectedWidget.value.options.axisXLabelColor = val
  }
})

const tooltips = computed({
  get() {
    return selectedWidget.value?.options.tooltips ?? true
  },
  set(val) {
    selectedWidget.value.options.tooltips = val
  }
})

const tooltipFontSize = computed({
  get() {
    return selectedWidget.value?.options.tooltipFontSize ?? '20px'
  },
  set(val) {
    selectedWidget.value.options.tooltipFontSize = val
  }
})

const tooltipFontColor = computed({
  get() {
    return selectedWidget.value?.options.tooltipFontColor ?? ''
  },
  set(val) {
    selectedWidget.value.options.tooltipFontColor = val
  }
})

const dataLabelOptions = ref(['Name', 'Value', 'Both'])
const labelLocationOptions = ref(['Inside', 'Outside'])
const dataLabelType = computed({
  get() {
    return selectedWidget.value?.options.dataLabelType ?? 'Name'
  },
  set(val) {
    selectedWidget.value.options.dataLabelType = val
  }
})

const dataLabelLocation = computed({
  get() {
    return selectedWidget.value?.options.dataLabelLocation ?? 'Inside'
  },
  set(val) {
    selectedWidget.value.options.dataLabelLocation = val
  }
})

const dataLabels = computed({
  get() {
    return selectedWidget.value?.options.dataLabels ?? true
  },
  set(val) {
    selectedWidget.value.options.dataLabels = val
  }
})

const dataLabelsColor = computed({
  get() {
    return selectedWidget.value?.options.dataLabelsColor ?? '#000'
  },
  set(val) {
    selectedWidget.value.options.dataLabelsColor = val
  }
})

const stackLabels = computed({
  get() {
    return selectedWidget.value?.options.stackLabels ?? false
  },
  set(val) {
    selectedWidget.value.options.stackLabels = val
  }
})

const aiButton = computed({
  get: () => selectedWidget.value?.aiButton ?? false,
  set: val => selectedWidget.value && (selectedWidget.value.aiButton = val)
})

const fontSizeOptions = [
  '12px', '14px', '16px', '18px', '20px', '22px', '24px', '28px', '32px',
]

const dataTypeOptions = [
  { value: 'value-drivers', name: 'Value Drivers' },
  { value: 'custom', name: 'Custom' }
]

const dataTypeChosen = computed({
  get() {
    if (Object.keys(selectedWidget.value?.options).length === 0) {
      selectedWidget.value.options.dataType = 'value-drivers'
    } else if (!('dataType' in selectedWidget.value.options)) {
      selectedWidget.value.options.dataType = 'custom'
    }

    return selectedWidget.value?.options.dataType
  },
  set(val) {
    selectedWidget.value.options.dataType = val
  }
})

const mapThemeColorsToData = () => {
  //TODO: make this also update as new data points are added to a chart series
  const data = selectedWidget.value.data.series.filter(row => row.some(cell => cell !== "")).length
  const themeColorsLen = theme.value.themeColors.length
  if (selectedWidget.value.themeColor.length === 1 && selectedWidget.value.themeColor[0] === "#5b9bd5") {
    if (data > themeColorsLen) {
      const firstThemeColor = theme.value.themeColors[0]
      const firstThemeColorHex = tinycolor(firstThemeColor).toHexString()
      const additionalColors = data - themeColorsLen
      const colors = generateColors(firstThemeColorHex, additionalColors + 1)

      selectedWidget.value.themeColor = [...theme.value.themeColors, ...colors]
    }
  }
}

const generateColors = (baseColorHex: string, numberOfColors: number, hueStep = 60) => {
  const baseColor = tinycolor(baseColorHex)
  const colors = []

  for (let i = 0; i < numberOfColors; i++) {
    const newColor = baseColor.spin(hueStep * i).toHexString()
    colors.push(newColor)
  }

  return colors.slice(1)
}

watch(selectedWidget, () => {
  if (!selectedWidget.value || selectedWidget.value.type !== 'chart') return
  fill.value = selectedWidget.value.fill || '#fff'
  if (selectedWidget.value.options) {
    const {
      lineSmooth: _lineSmooth,
      showLine: _showLine,
      showArea: _showArea,
      horizontalBars: _horizontalBars,
      donut: _donut,
      stackBars: _stackBars,
    } = selectedWidget.value.options

    if (_lineSmooth !== undefined) lineSmooth.value = _lineSmooth as boolean
    if (_showLine !== undefined) showLine.value = _showLine
    if (_showArea !== undefined) showArea.value = _showArea
    if (_horizontalBars !== undefined) selectedWidget.value.options.horizontalBars = _horizontalBars
    if (_donut !== undefined) donut.value = _donut
    if (_stackBars !== undefined) selectedWidget.value.options.stackBars = _stackBars
    if (selectedWidget.value.options.axisY?.showLabel !== undefined) yAxis.value = selectedWidget.value.options?.axisY?.showLabel
    if (selectedWidget.value.options.axisX?.showLabel !== undefined) xAxis.value = selectedWidget.value.options?.axisX?.showLabel
  }
  mapThemeColorsToData()
  themeColor.value = selectedWidget.value.themeColor
  compactValue.value = selectedWidget.value.compact

}, {deep: true, immediate: true})

const chartData = computed(() => selectedWidget.value.data.series)

watch(chartData, (newValue, oldValue) => {
  if (JSON.stringify(newValue) !== JSON.stringify(oldValue)) {
    const dataLen = newValue.filter(row => row.some(cell => cell !== "")).length
    const chartThemeColorsLen = themeColor.value.length
    if (dataLen === chartThemeColorsLen) return

    if (dataLen > chartThemeColorsLen) {
      const newColors = generateColors(themeColor.value.at(-1), dataLen - chartThemeColorsLen + 1)
      themeColor.value = themeColor.value.concat(newColors)
      selectedWidget.value.themeColor = selectedWidget.value.themeColor.concat(newColors)
    }

    if (dataLen < chartThemeColorsLen) {
      deleteThemeColor(chartThemeColorsLen - 1)
    }
  }
})

// Set other options: Convert column chart to bar chart, line chart to area chart, line chart to scatter chart, pie chart to ring chart, line chart to switch smooth curve
const updateOptions = (optionProps: ChartOptions) => {
  const _handleElement = selectedWidget.value
  selectedWidget.value.options = {..._handleElement.options, ...optionProps}
}

const previousBarChartStyle = ref(barChartStyle.value)
const updateBarChartStyle = (event: any) => {
  if (previousBarChartStyle.value) updateOptions({[previousBarChartStyle.value]: false})
  previousBarChartStyle.value = event
  if (event) updateOptions({[event]: true})
}

const previousLineChartStyle = ref(lineChartStyle.value)
const updateLineChartStyle = (event: any) => {
  if (previousLineChartStyle.value) updateOptions({[previousLineChartStyle.value]: false})
  previousLineChartStyle.value = event
  if (event) updateOptions({[event]: true})
}

const showPriceValue = computed({
  get() {
    return selectedWidget.value?.options.showPriceValue ?? true
  },
  set(val) {
    selectedWidget.value.options.showPriceValue = val
  }
})

const filterOutZeros = computed({
  get() {
    return selectedWidget.value?.options.filterOutZeros ?? true
  },
  set(val) {
    selectedWidget.value.options.filterOutZeros = val
  }
})

const filterOutNegatives = computed({
  get() {
    return selectedWidget.value?.options.filterOutNegatives ?? true
  },
  set(val) {
    selectedWidget.value.options.filterOutNegatives = val
  }
})

const updateNumberFormat = computed({
  get() {
    return selectedWidget.value?.compact ?? false
  },
  set(val) {
    selectedWidget.value.compact = val
  }
})

// Set theme color
const updateTheme = (color: string, index: number) => {
  selectedWidget.value.themeColor = themeColor.value.map((c, i) => i === index ? color : c)
}

// Add theme color
const addThemeColor = () => {
  selectedWidget.value.themeColor = [...themeColor.value, theme.value.themeColor]
}

// Use preset color schemes
const applyPresetTheme = (colors: string[], index: number) => {
  selectedWidget.value.themeColor = colors.slice(0, index + 1)
  presetThemesVisible.value = false
}

// Delete theme color
const deleteThemeColor = (index: number) => {
  selectedWidget.value.themeColor = themeColor.value.filter((c, i) => i !== index)
}

const updateLabels = (value, styleProp) => {
  const newLabelStyle = {}

  if (typeof value === 'string') {
    newLabelStyle[styleProp] = value;
  } else if (typeof value === 'object' && 'value' in value) {
    newLabelStyle[styleProp] = value.value;
  }

  selectedWidget.value.labelStyle = {...selectedWidget.value.labelStyle, ...newLabelStyle};
}

//Forces jspreadsheet to repopulate with new values if user is
//editing data within expanded jspreadsheet view
const editorKey = ref(0)
watch(chartDataEditorVisible, (newValue, oldValue) => {
  if (!newValue && oldValue) {
    editorKey.value++
  }
})

const openDataEditor = () => chartDataEditorVisible.value = true
emitter.on(EmitterEvents.OPEN_CHART_DATA_EDITOR, openDataEditor)

onUnmounted(() => {
  emitter.off(EmitterEvents.OPEN_CHART_DATA_EDITOR, openDataEditor)
})
</script>

<style lang="scss" scoped>
.i-icon {
  z-index: 1;
}

:deep( .p-button.p-component ) {
  justify-content: center;
}

.group {
  margin-bottom: 10px;
}

.chart-style-panel {
  user-select: none;
  padding-top: 20px;
}

.row {
  width: 100%;
  display: flex;
  align-items: center;
  margin-bottom: 10px;
}

.row-gap {
  width: 100%;
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 10px;
}

.full-width-btn {
  width: 100%;
}

.btn-icon {
  margin-right: 3px;
}

.color-btn-wrap {
  position: relative;
}

.delete-color-btn {
  position: absolute;
  width: 30px;
  right: 2px;
  top: 2px;
  bottom: 2px;
  display: flex;
  justify-content: center;
  align-items: center;
  background-color: #fff;
  cursor: pointer;
}

.preset-themes {
  width: 200px;
  display: flex;
  margin-bottom: -10px;
  flex-direction: column;

  @include flex-grid-layout();
}

.preset-theme {
  display: flex;
  cursor: pointer;

  @include flex-grid-layout-children(2, 48%);
}

.preset-theme-color {
  width: 20px;
  height: 20px;

  &.select {
    transform: scale(1.2);
    transition: transform $transitionDelayFast;
  }
}

.chk-box {
  display: flex;
  align-items: center;
  margin-bottom: 10px;
}

.chk-box label {
  margin-left: 5px; /* Adjust the gap as needed */
}

.btn-group {
  display: flex;
  justify-content: center;
  align-items: center;
}

:deep( #padding-input > .p-inputtext.p-component.p-inputnumber-input ) {
  margin-right: 5px;
  width: 70px;
  margin-top: 20px;
}

.chart-padding-label {
  top: 0;
  position: absolute;
}

.dimension-input :deep(.p-inputtext) {
  width: 110px;
}

span.label {
  display: inline-block;
  width: 60px
}

span.label-long {
  display: inline-block;
  width: 100px
}

span.label-x-long {
  display: inline-block;
  width: 120px
}

.style-section {
  margin-top: 20px;
  margin-bottom: 20px;
}

.style-row {
  margin-top: 5px;
  width: 300px;
  align-items: center;
  display: flex
}

:deep(.p-highlight span) {
  z-index: 100;
}

:deep(.p-selectbutton .p-button::before) {
  transition: none !important;
}

:deep(.p-tag) {
  margin-left: 5px;
}

:deep(.p-tag-value) {
  line-height: 1
}

:deep(.chk-box) {
  display: flex;
  align-items: center;
  margin-bottom: 10px;
}

:deep(.chk-box label) {
  margin-left: 5px;
}

:deep(.p-button) {
  background: #fff;
  border: 1px solid #ccc;
  color: black;
}

.dimension-input :deep(.p-inputtext) {
  width: 50px;
}

.smaller-switch {
  transform: scale(0.6);
  display: inline-block;
  vertical-align: middle;
  margin-right: 10px;
}

.border-container {
  width: 300px;
  display: flex;
  align-items: center;
  color: #666
}

.trash {
  margin-left: 5px;
}

:deep(.p-panel-header) {
  padding-left: 8px !important;
  padding-right: 8px !important;
  background: transparent;
  border-bottom-color: v-bind(headerBorderColor);
}

.border-inputs :deep(.p-inputtext) {
  width: 50px;
}

:deep(.p-hidden-accessible) {
  border: 0;
  clip: rect(0 0 0 0);
  height: 1px;
  margin: -1px;
  overflow: hidden;
  padding: 0;
  position: absolute;
  width: 1px;
}
</style>
